/**
 * Capacity attention hook (C14).
 *
 * Fetches the server-aggregated, severity-ranked list of forward-looking capacity
 * problems for the org. The endpoint does ALL the aggregation and ordering: consumers
 * render `items` in the returned `rank` order and must never count, group, derive or
 * re-sort them client-side.
 *
 * There is ONE sanctioned narrowing of that ban: an O(1) lookup index keyed by the
 * server-provided `group_id` (built in `capacityAttentionIndex.ts`, consumed at the grid
 * level to place per-group attention glyphs). Looking an item up by a server field is NOT
 * "deriving": severity, counts, ranking and the grouping-into-items all stay server-side
 * and render verbatim. Only re-computing any of those (severity from a percentage,
 * re-ranking, re-grouping, or per-group re-fetching) remains forbidden — a per-group fetch
 * would reintroduce the N+1 that this single-query endpoint exists to avoid.
 *
 * Cache notes:
 * - Read-only, so nothing here invalidates. Later mutations that move or re-allocate a
 *   child SHOULD invalidate `capacityKeys.attention(...)` so the strip reflects the move.
 * - The endpoint's cost centre is the server-side categorization pass (it walks every
 *   active child), so a 5-minute staleTime keeps it off the hot path. If it ever becomes
 *   a problem, a count-only query is the follow-up.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { keepPreviousDataForOrg } from '@/lib/tenantPlaceholder';
import { CapacityService } from '@/api/services/CapacityService';
import { capacityKeys } from './useCapacity';
import type { CapacityAttentionResponse } from '@/types/capacity';

/** D5: the attention horizon defaults to 60 days; the server clamps anything above 90. */
export const DEFAULT_ATTENTION_HORIZON_DAYS = 60;

export interface UseCapacityAttentionArgs {
  /** Childcare id to scope the items to. Omit for the whole org. */
  location?: string;
  /** Forward horizon in days (default 60, server-clamped to 90). */
  horizonDays?: number;
  /** False skips the request (a user without the capacity module). */
  enabled?: boolean;
}

export function useCapacityAttention({
  location,
  horizonDays = DEFAULT_ATTENTION_HORIZON_DAYS,
  enabled = true,
}: UseCapacityAttentionArgs = {}) {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacityKeys.attention(orgId || '', location, horizonDays),
    queryFn: async () => {
      const response = await CapacityService.getApiV1CapacityAttention(location, horizonDays);
      return response as CapacityAttentionResponse;
    },
    // Keep the previous scope's items on screen while the next load resolves — but only
    // within the same tenant; an org switch must resolve to a real loading state rather
    // than repaint the previous tenant's children.
    placeholderData: keepPreviousDataForOrg<CapacityAttentionResponse>(orgId),
    enabled: enabled && isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('404') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
